import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { mockShalaSubAdminMenu } from 'src/app/core/data/menu.data';
import { HttpRequestService } from 'src/app/core/services';
import {
  NzFormatEmitEvent,
  NzTreeComponent,
  NzTreeNodeOptions,
} from 'ng-zorro-antd/tree';

@Component({
  selector: 'app-add-update-sub-admin',
  templateUrl: './add-update-sub-admin.component.html',
  styleUrls: ['./add-update-sub-admin.component.scss'],
})
export class AddUpdateSubAdminComponent implements OnInit {
  @ViewChild('nzTreeComponent', { static: false })
  nzTreeComponent!: NzTreeComponent;

  addUpdateSubAdminForm: FormGroup;
  addUpdateSubAdminPasswordForm: FormGroup;
  idForUpdate: string;
  buttonLoading = false;

  viewReEnterPassword = false;
  viewPassword = false;

  menuData: NzTreeNodeOptions[] = [];
  selectedKeys: string[] = [];
  CheckedValue: string[] = [];

  childKeyArray = [
    'editorials',
    'subjects',
    'chapters',
    'topics',
    'exam',
    'exam-category',
    'success-stories',
    'current-affairs',
    'resource',
    'testimonial',
  ];

  userChildKeyArray = [
    'job-alerts',
    'push-notifications',
    'announcement',
    'support-details',
  ];

  constructor(
    private fb: FormBuilder,
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {
    this.idForUpdate = this.activatedRoute.snapshot.params.id;

    this.addUpdateSubAdminForm = this.fb.group({
      name: [null, Validators.required],
      allowedRoute: [null],
      email: [
        null,
        [
          Validators.required,
          Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
        ],
      ],
      mobile: [null, [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      role: ['subAdmin'],
      status: [true],
    });

    this.addUpdateSubAdminPasswordForm = this.fb.group({
      password: [null, Validators.required],
      reEnterPassword: [
        null,
        [Validators.required, this.confirmationValidator],
      ],
    });
  }

  get mobile() {
    return this.addUpdateSubAdminForm.get('mobile');
  }

  ngOnInit(): void {
    this.menuData = mockShalaSubAdminMenu;

    // default selection
    this.CheckedValue = ['dashboard'];
    this.selectedKeys = ['dashboard'];

    if (this.idForUpdate) {
      this.getSubAdminsById();
    }
  }

  onCheckBoxChange(event: NzFormatEmitEvent): void {
    // event.nodes is the tree's full, authoritative list of currently
    // checked nodes (already accounts for parent/child cascade checks),
    // so replace selectedKeys wholesale instead of patching it in/out --
    // patching missed cascade unchecks (e.g. unchecking 'User' left its
    // stale children keys behind in selectedKeys).
    this.selectedKeys = (event.nodes || []).map((node: any) => node.key);
  }

  // password validator
  confirmationValidator = (control: FormControl): any => {
    if (!control.value) {
      return { required: true };
    } else if (
      control.value !==
      this.addUpdateSubAdminPasswordForm?.controls.password.value
    ) {
      return { confirm: true, error: true };
    }
    return {};
  };

  updateConfirmValidator(): void {
    Promise.resolve().then(() =>
      this.addUpdateSubAdminPasswordForm.controls.reEnterPassword.updateValueAndValidity(),
    );
  }

  getSubAdminsById(): void {
    this.httpRequestService
      .request('get', `auth-admins/${this.idForUpdate}`)
      .subscribe(
        (result: any) => {
          const data = result.data;

          this.CheckedValue = data.allowedRoute || [];
          this.selectedKeys = [...this.CheckedValue];

          this.addUpdateSubAdminForm.patchValue({
            name: data.name,
            email: data.email,
            mobile: data.mobile,
            status: data.status,
          });

          // The tree checks a parent whenever its key is present in
          // nzCheckedKeys, and checking a parent visually cascades the
          // check down to ALL of its current children -- including ones
          // (e.g. push-notifications) added after this sub-admin's
          // allowedRoute was last saved. That makes the checkbox look
          // checked without it actually being in `selectedKeys`, so
          // saving again silently drops it. Re-sync selectedKeys from
          // the tree's real conducted state once it has rendered.
          setTimeout(() => {
            if (this.nzTreeComponent) {
              this.selectedKeys = this.nzTreeComponent
                .getCheckedNodeList()
                .map((node: any) => node.key);
            }
          });
        },
        () => {},
      );
  }

  submit(): void {
    if (!this.addUpdateSubAdminForm.valid) {
      this.markFormGroupTouched(this.addUpdateSubAdminForm);
      this.markFormGroupTouched(this.addUpdateSubAdminPasswordForm);

      if (!this.selectedKeys || this.selectedKeys.length === 0) {
        this.notificationService.error('', 'Please select a Route');
      }
      return;
    }

    if (!this.selectedKeys || this.selectedKeys.length === 0) {
      this.notificationService.error('', 'Please select a Route');
      return;
    }

    if (this.idForUpdate) {
      this.addOrUpdateSubAdmin(
        'put',
        `auth-admins/sub-admin-profile/${this.idForUpdate}`,
        'Sub Admin Successfully Updated',
      );
    } else {
      this.addOrUpdateSubAdmin(
        'post',
        'auth-admins/register',
        'Sub Admin Added Successfully',
      );
    }
  }

  addOrUpdateSubAdmin(
    requestMethod: string,
    requestURL: string,
    successMessage: string,
  ): void {
    this.buttonLoading = true;

    let finalKeys = [...this.selectedKeys];

    // ✅ handle masters
    if (finalKeys.includes('masters')) {
      finalKeys = [...new Set([...finalKeys, ...this.childKeyArray])];
    }

    // ✅ handle User (ensures all User sub-routes, e.g. push-notifications, are persisted)
    if (finalKeys.includes('User')) {
      finalKeys = [...new Set([...finalKeys, ...this.userChildKeyArray])];
    }

    this.addUpdateSubAdminForm.patchValue({
      allowedRoute: finalKeys,
    });

    const finalData = {
      name: this.addUpdateSubAdminForm.value.name,
      email: this.addUpdateSubAdminForm.value.email,
      password: this.addUpdateSubAdminPasswordForm.value.password,
      mobile: this.addUpdateSubAdminForm.value.mobile,
      role: this.addUpdateSubAdminForm.value.role,
      status: this.addUpdateSubAdminForm.value.status,
      allowedRoute: finalKeys,
    };

    this.httpRequestService
      .request(requestMethod, requestURL, finalData)
      .subscribe(
        () => {
          this.notificationService.success('', successMessage);
          this.router.navigateByUrl('/main/sub-admin');
          this.buttonLoading = false;
        },
        (error: any) => {
          if (error.error?.errors) {
            Object.values(error.error.errors).forEach((err: any) => {
              this.notificationService.error('', err);
            });
          } else {
            this.notificationService.error('', error.error.message);
          }
          this.buttonLoading = false;
        },
      );
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsDirty();
      control.updateValueAndValidity();
    });
  }
}
